import { useCallback } from 'react';
import {
  findAllTaskListItems,
  getFindAllTaskListItemsQueryKey,
  TaskStatusUnionType,
  useUpdateTask,
} from '@api';
import { useNotification } from '@common';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.UPDATE_TASK_STATUS';
const TASKS_QUERY_KEY = getFindAllTaskListItemsQueryKey();

type TasksListCacheType = Awaited<ReturnType<typeof findAllTaskListItems>>;

type UpdateTaskStatusContextType = {
  previous: TasksListCacheType | undefined;
};

type UseUpdateTaskStatusReturnType = {
  closeTask: (taskId: string) => void;
  reopenTask: (taskId: string) => void;
};

export const useUpdateTaskStatus = (): UseUpdateTaskStatusReturnType => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const updateCache = useCallback(
    async (taskId: string, status: TaskStatusUnionType) => {
      await queryClient.cancelQueries({ queryKey: TASKS_QUERY_KEY });

      const previous =
        queryClient.getQueryData<TasksListCacheType>(TASKS_QUERY_KEY);

      queryClient.setQueryData<TasksListCacheType>(
        TASKS_QUERY_KEY,
        (current) => {
          if (!current) return current;

          return {
            ...current,
            data: current.data.map((task) =>
              task.id === taskId ? { ...task, status } : task
            ),
          };
        }
      );

      return { previous };
    },
    [queryClient]
  );

  const { mutate } = useUpdateTask<AxiosError, UpdateTaskStatusContextType>({
    mutation: {
      onMutate: async ({ taskId, data }) => {
        if (!data?.status) return { previous: undefined };

        return updateCache(taskId, data.status);
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(TASKS_QUERY_KEY, context?.previous);

        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      },
    },
  });

  const closeTask = useCallback(
    (taskId: string) => {
      mutate({ taskId, data: { status: 'CLOSED' } });
    },
    [mutate]
  );

  const reopenTask = useCallback(
    (taskId: string) => {
      mutate({ taskId, data: { status: 'OPEN' } });
    },
    [mutate]
  );

  return { closeTask, reopenTask };
};
